import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import BoltShiningText from './BoltShiningText';

export const BOLT_TOOL_LABELS = {
  get_recent_safety_events: 'safety events',
  get_open_hazards: 'open hazards',
  get_overdue_actions: 'overdue actions',
  get_jobsite_summary: 'jobsite summary',
  get_worker_context: 'worker details',
  get_worker_risk_profile: 'risk profile',
  get_jobsite_hazards: 'jobsite hazards',
  get_pending_permits: 'pending permits',
  get_expiring_permits: 'expiring permits',
  check_task_permit_requirements: 'permit requirements',
  get_insurance_metrics: 'insurance metrics',
  get_worker_checklist_progress: 'checklist progress',
  get_training_content: 'training content',
  retrieve_safety_knowledge: 'safety knowledge',
  create_action_item: 'creating action item',
  get_workers_list: 'workers',
  get_training_status: 'training status',
  get_open_tasks: 'open tasks',
  get_compliance_summary: 'compliance data',
  get_shift_attendance: 'shift attendance',
  get_crew_training_detail: 'crew training',
  get_org_overview: 'organization overview',
  resolve_entity: 'resolving names',
  query_trade_library: 'trade library',
  get_trade_calculators: 'trade calculators',
  get_trade_reference: 'code reference',
  query_blueprint_content: 'blueprint content',
  get_building_locations: 'building locations',
  get_blueprint_summary: 'blueprint summary',
  planning_questions: 'preparing questions',
};

export function describeBoltTool(name) {
  return BOLT_TOOL_LABELS[name] || String(name || '').replace(/_/g, ' ');
}

export function formatThoughtDuration(ms) {
  if (!ms || ms < 500) return 'Thought';
  const seconds = Math.max(1, Math.round(ms / 1000));
  return `Thought for ${seconds}s`;
}

export default function BoltReasoning({ parts = [], active = false, durationMs = 0 }) {
  const [open, setOpen] = useState(active);
  const bodyRef = useRef(null);
  const thoughtText = parts
    .filter((part) => part.type === 'thought')
    .map((part) => part.text)
    .join('');
  const tools = parts.filter((part) => part.type === 'tool');
  const hasBody = Boolean(thoughtText.trim() || tools.length);

  useEffect(() => {
    setOpen(active);
  }, [active]);

  useEffect(() => {
    if (!open || !active || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [active, open, thoughtText]);

  if (!parts.length && !active) return null;

  return (
    <div
      className={cn('bolt-reasoning', active && 'is-active', open && 'is-open')}
      data-testid="bolt-reasoning"
    >
      <button
        type="button"
        className="bolt-reasoning__trigger"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <ChevronRight
          size={13}
          className={cn('bolt-reasoning__chev', open && 'is-open')}
          aria-hidden
        />
        <span className="bolt-reasoning__label">
          {active ? <BoltShiningText text="Thinking" /> : formatThoughtDuration(durationMs)}
        </span>
      </button>
      {open && hasBody && (
        <div ref={bodyRef} className="bolt-reasoning__body">
          {thoughtText ? (
            <p className="bolt-reasoning__monologue">{thoughtText}</p>
          ) : null}
          {tools.map((part, index) => (
            <p key={`${part.name || 'tool'}-${index}`} className="bolt-reasoning__tool">
              {part.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
